/**
 * LpgSearchModal — extracted from Chat.jsx (Feb 2026). Global search
 * across LPG ID / Consumer ID / Mobile with Jump-to-card. Business
 * logic UNCHANGED — pure code move.
 *
 * SingleHitList is co-located (internal helper) since it's only ever
 * rendered by LpgSearchModal. Kept in the same file for locality.
 */
import React, { useState } from "react";
import { Search, X, Loader2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { copyForSpreadsheet } from "@/lib/clipboard";

function LpgSearchModal({ onClose, onJumpTo }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async (input) => {
    const raw = (input || "").trim();
    if (!raw) {
      setErr("LPG ID(s) paste karein");
      return;
    }
    // Accept any 6-20 digit token — HPCL short (6), BharatGas tail (8-9),
    // IndianOil (16), BharatGas full (17). Backend auto-expands 8-9 digit
    // tails to the 17-digit BharatGas form before lookup.
    const numbers = raw.match(/\d{6,20}/g) || [];
    if (numbers.length === 0) {
      setErr("Kam se kam 6 digit ka LPG ID type karein");
      return;
    }
    setBusy(true);
    setErr("");
    setResult(null);
    setBulkResult(null);
    try {
      if (numbers.length > 1) {
        const { data } = await api.post("/lpg/search-bulk", { lpg_ids: numbers });
        setBulkResult(data);
      } else {
        const { data } = await api.get(`/lpg/search?lpg_id=${encodeURIComponent(numbers[0])}`);
        setResult(data);
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    run(q);
  };

  const labelForStatus = (s) => {
    if (s === "correct") return { txt: "Correct", cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" };
    if (s === "wrong") return { txt: "Wrong", cls: "bg-red-500/15 text-red-700 border-red-500/30" };
    if (s === "resent") return { txt: "Resent", cls: "bg-blue-500/15 text-blue-700 border-blue-500/30" };
    return { txt: s || "—", cls: "bg-gray-500/15 text-gray-700 border-gray-500/30" };
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
      onClick={onClose}
      data-testid="lpg-search-modal"
    >
      <div
        className="bg-white rounded-lg w-full max-w-3xl border border-[#DCDCD3] shadow-xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#DCDCD3]">
          <div>
            <h2
              className="text-lg font-semibold text-[#1C1C19] flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <Search className="w-5 h-5 text-[#385C4B]" />
              Global Search · LPG / Consumer / Mobile
            </h2>
            <p className="text-xs text-[#6B6B61] mt-0.5">
              Type ANY of: <b>16-digit LPG ID</b>, <b>Consumer ID</b> (6-9 digit HPCL/Bharat tail), or <b>10-digit Mobile</b>.
              Har hit ke aage <b>Jump</b> button hai jo directly delivery card pe le jayega — poori history + date instantly.
            </p>
          </div>
          <button onClick={onClose} className="text-[#6B6B61] hover:text-[#1C1C19] p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 border-b border-[#DCDCD3] flex flex-col gap-2">
          <textarea
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="LPG ID · Consumer ID · Mobile — koi bhi paste karein&#10;7500000071726432&#10;9812345670  (10-digit mobile)&#10;121750825  (Consumer ID)"
            rows={5}
            data-testid="lpg-search-input"
            className="w-full px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono resize-y focus:outline-none focus:border-[#385C4B]"
            style={{ fontFamily: "Inter, sans-serif" }}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-[#6B6B61]">
              Detected: <b>{(q.match(/\d{6,20}/g) || []).length}</b> LPG ID(s)
            </span>
            <button
              type="submit"
              disabled={busy || !q.trim()}
              data-testid="lpg-search-submit"
              className="px-4 py-2 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </button>
          </div>
        </form>

        {err && (
          <div className="mx-4 mt-3 bg-[#8A6046]/10 border border-[#8A6046]/30 text-[#8A6046] rounded px-3 py-2 text-sm">
            {err}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {/* Single-result blocks (unchanged) */}
          {result && result.total === 0 && (
            <div
              className="text-center py-10 bg-[#F5F5F0] rounded-lg border border-[#DCDCD3]"
              data-testid="lpg-search-empty"
            >
              <Search className="w-8 h-8 mx-auto text-[#8A6046] mb-3" strokeWidth={1.5} />
              <div
                className="text-sm font-semibold text-[#1C1C19]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Is LPG ID ka DAC humne nahi bheja hai
              </div>
              <div className="text-xs text-[#6B6B61] mt-1 px-6 leading-relaxed">
                <span className="font-mono">{result.lpg_id}</span> ke liye
                master record me koi entry nahi mili.
              </div>
            </div>
          )}

          {result && result.total > 0 && (
            <>
              <div className="bg-[#385C4B]/10 border border-[#385C4B]/30 text-[#385C4B] rounded-md px-3 py-2 text-xs mb-3 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <b>Haan</b>, is LPG ID ka DAC humne bheja hai —{" "}
                  {result.total} entry{result.total > 1 ? "s" : ""} mili
                  master record me.
                </span>
              </div>
              <div className="text-xs font-mono text-[#6B6B61] mb-2 tracking-[0.2em] uppercase">
                {result.total} match{result.total > 1 ? "es" : ""} found
              </div>
              <SingleHitList hits={result.hits} labelForStatus={labelForStatus} onJumpTo={onJumpTo} />
            </>
          )}

          {/* Bulk result */}
          {bulkResult && (
            <BulkResultView
              data={bulkResult}
              labelForStatus={labelForStatus}
              onCopyAll={async (rows) => {
                await copyForSpreadsheet(rows);
              }}
            />
          )}

          {!result && !bulkResult && !err && (
            <div className="text-xs text-[#6B6B61] text-center py-10">
              LPG ID · Consumer ID · Mobile — koi bhi type/paste karein. Multiple values ek saath bhi paste kar sakte hain (newline / tab / space se separated).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Shared single-hit list (used by single search and history sublist if needed)
function SingleHitList({ hits, labelForStatus, onJumpTo }) {
  return (
    <div className="space-y-2">
      {hits.map((h) => {
        const lab = labelForStatus(h.status);
        return (
          <div
            key={`${h.message_id}::${h.entry_index}`}
            data-testid="lpg-search-hit"
            className="bg-[#F5F5F0] border border-[#DCDCD3] rounded-md p-3 flex items-start gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="text-sm font-semibold text-[#1C1C19]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {h.agency}
                </span>
                <span
                  className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded border ${lab.cls}`}
                >
                  {lab.txt}
                </span>
                <span className="text-[10px] font-mono text-[#6B6B61]">{h.date}</span>
              </div>
              <div
                className="mt-1 text-xs font-mono"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                <span className="text-[#6B6B61]">LPG </span>
                <span className="text-[#1C1C19]">{h.lpg_id}</span>
                {h.mobile && (
                  <>
                    <span className="text-[#6B6B61] ml-3">Mob </span>
                    <span className="text-[#1C1C19]">{h.mobile}</span>
                  </>
                )}
                <span className="text-[#6B6B61] ml-3">DAC </span>
                <span className="font-bold text-[#385C4B]">{h.dac_code}</span>
              </div>
              <div className="mt-1 text-[10px] text-[#6B6B61]">
                Sent by <b>{h.sender}</b>
                {h.uploaded_by && h.uploaded_by !== h.sender && (
                  <> · uploaded by <b>{h.uploaded_by}</b></>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              {onJumpTo && (
                <button
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onJumpTo(h);
                  }}
                  data-testid="lpg-search-jump"
                  title="Jump to this delivery card in Chat"
                  className="px-2.5 py-1 text-[10px] font-mono font-semibold rounded-md bg-[#385C4B] text-white hover:bg-[#2D4A3C] flex items-center gap-1"
                >
                  Jump →
                </button>
              )}
              <button
                onClick={async (ev) => {
                  ev.stopPropagation();
                  const btn = ev.currentTarget;
                  const ok = await copyForSpreadsheet([
                    [h.lpg_id, h.mobile || "", h.dac_code, h.status, h.agency, h.date],
                  ]);
                  btn.setAttribute(ok ? "data-copied" : "data-copy-failed", "1");
                  setTimeout(() => {
                    btn.removeAttribute("data-copied");
                    btn.removeAttribute("data-copy-failed");
                  }, 1500);
                }}
                data-testid="lpg-search-copy"
                title="Copy row (spreadsheet-safe)"
                className="p-1.5 rounded text-[#6B6B61] hover:text-[#385C4B] hover:bg-[#385C4B]/10 data-[copied=1]:bg-emerald-500/30 data-[copy-failed=1]:bg-red-500/30 transition-colors"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// BulkResultView — used when user pastes multiple LPG IDs into the search box.
// Renders per-status summary grid + matched (with Copy-all) + unmatched blocks.
function BulkResultView({ data, labelForStatus, onCopyAll }) {
  const matched = data.matched || [];
  const unmatched = data.unmatched || [];
  return (
    <div className="space-y-5" data-testid="lpg-bulk-result">
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#F5F5F0] border border-[#DCDCD3] rounded p-3 text-center">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">Queried</div>
          <div
            className="text-xl font-semibold text-[#1C1C19] mt-0.5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {data.queried}
          </div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 text-center">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-emerald-700">Matched</div>
          <div
            className="text-xl font-semibold text-emerald-700 mt-0.5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {data.matched_count}
          </div>
        </div>
        <div className="bg-[#8A6046]/10 border border-[#8A6046]/30 rounded p-3 text-center">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#8A6046]">Not Found</div>
          <div
            className="text-xl font-semibold text-[#8A6046] mt-0.5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {data.unmatched_count}
          </div>
        </div>
      </div>

      {matched.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-mono tracking-[0.2em] uppercase text-emerald-700">
              ✓ Matched — DAC humne bheja hai ({matched.length})
            </h3>
            <button
              data-testid="lpg-bulk-copy-matched"
              onClick={(ev) => {
                const rows = [
                  ["LPG ID", "DAC", "Status", "Agency", "Date", "Sent By"],
                  ...matched.map((h) => [h.lpg_id, h.dac_code, h.status, h.agency, h.date, h.sender]),
                ];
                const btn = ev.currentTarget;
                onCopyAll(rows).then(() => {
                  btn.setAttribute("data-copied", "1");
                  setTimeout(() => btn.removeAttribute("data-copied"), 1500);
                });
              }}
              className="text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded border border-[#DCDCD3] text-[#385C4B] hover:bg-[#385C4B]/5 data-[copied=1]:bg-emerald-500/30"
            >
              <Copy className="w-3 h-3 inline mr-1" /> Copy all
            </button>
          </div>
          <SingleHitList hits={matched} labelForStatus={labelForStatus} />
        </div>
      )}

      {unmatched.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-mono tracking-[0.2em] uppercase text-[#8A6046]">
              ✗ Not Found — DAC abhi tak nahi bheja ({unmatched.length})
            </h3>
            <button
              data-testid="lpg-bulk-copy-unmatched"
              onClick={(ev) => {
                const rows = unmatched.map((lpg) => [lpg]);
                const btn = ev.currentTarget;
                onCopyAll(rows).then(() => {
                  btn.setAttribute("data-copied", "1");
                  setTimeout(() => btn.removeAttribute("data-copied"), 1500);
                });
              }}
              className="text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded border border-[#DCDCD3] text-[#8A6046] hover:bg-[#8A6046]/5 data-[copied=1]:bg-emerald-500/30"
            >
              <Copy className="w-3 h-3 inline mr-1" /> Copy IDs
            </button>
          </div>
          <div className="bg-[#F5F5F0] border border-[#DCDCD3] rounded p-3">
            <div
              className="font-mono text-xs text-[#1C1C19] grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1"
              style={{ fontFamily: "Inter, sans-serif" }}
              data-testid="lpg-bulk-unmatched-list"
            >
              {unmatched.map((lpg) => (
                <div key={lpg} className="truncate">{lpg}</div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LpgSearchModal;
